<script setup>
import { ref, computed, onMounted } from 'vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['selectSearch', 'clearAll']);

const MAX_RECENT_SEARCHES = 3;
const recentSearches = ref([]);

const loadRecentSearches = () => {
  const stored = LocalStorage.get(LOCAL_STORAGE_KEYS.RECENT_SEARCHES) || [];
  recentSearches.value = Array.isArray(stored) ? stored : [];
};

const saveRecentSearches = () => {
  LocalStorage.set(LOCAL_STORAGE_KEYS.RECENT_SEARCHES, recentSearches.value);
};

const addRecentSearch = query => {
  if (!query || query.trim().length < 2) return;

  const trimmedQuery = query.trim();

  const existingIndex = recentSearches.value.findIndex(
    search => search.toLowerCase() === trimmedQuery.toLowerCase()
  );

  if (existingIndex !== -1) {
    recentSearches.value.splice(existingIndex, 1);
  }

  recentSearches.value.unshift(trimmedQuery);

  if (recentSearches.value.length > MAX_RECENT_SEARCHES) {
    recentSearches.value = recentSearches.value.slice(0, MAX_RECENT_SEARCHES);
  }

  saveRecentSearches();
};

const clearRecentSearches = () => {
  recentSearches.value = [];
  LocalStorage.remove(LOCAL_STORAGE_KEYS.RECENT_SEARCHES);
};

const hasRecentSearches = computed(() => recentSearches.value?.length > 0);

const onSelectSearch = query => {
  emit('selectSearch', query);
};

const onClearAll = () => {
  clearRecentSearches();
  emit('clearAll');
};

defineExpose({
  addRecentSearch,
  loadRecentSearches,
});

onMounted(() => {
  loadRecentSearches();
});
</script>

<template>
  <div v-if="hasRecentSearches" class="w-full pt-2">
    <div
      class="w-full rounded-polaris-md border border-solid border-polaris-border bg-polaris-card p-1"
    >
      <div class="flex items-center justify-between gap-2 px-2 py-1">
        <div class="flex min-w-0 items-center gap-2">
          <Icon
            icon="i-lucide-rotate-ccw"
            class="size-4 flex-shrink-0 text-polaris-muted-foreground"
          />
          <h3
            class="m-0 truncate type-polaris-caption text-polaris-muted-foreground"
          >
            {{ $t('SEARCH.RECENT_SEARCHES') }}
          </h3>
        </div>
        <Button
          type="button"
          xs
          slate
          ghost
          @mousedown.prevent
          @click="onClearAll"
        >
          {{ $t('SEARCH.CLEAR_ALL') }}
        </Button>
      </div>

      <div class="flex flex-col">
        <button
          v-for="(search, index) in recentSearches"
          :key="search"
          type="button"
          class="group flex h-8 w-full items-center gap-2 rounded-polaris-sm border-0 bg-transparent px-2 text-left type-polaris-body text-polaris-foreground transition-colors duration-150 hover:bg-polaris-accent hover:text-polaris-accent-foreground"
          @mousedown.prevent
          @click="onSelectSearch(search)"
        >
          <Icon
            icon="i-lucide-search"
            class="size-4 flex-shrink-0 text-polaris-muted-foreground"
          />
          <span class="min-w-0 flex-1 truncate">{{ search }}</span>
          <span
            v-if="index === 0"
            class="flex-shrink-0 type-polaris-caption text-polaris-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          >
            {{ $t('SEARCH.MOST_RECENT') }}
          </span>
        </button>
      </div>
    </div>
  </div>
  <template v-else />
</template>
